/**
 * DrbPresentationModal — choose the drawings for the DRB presentation and zoom each slide to its
 * drawing (docs/DRB_PRESENTATION.md "Zoom to the drawing").
 *
 * Left: every sheet in the project's newest DAPC set, ticked when it goes in. Middle: the chosen
 * sheet (rendered with pdfium) with its crop box — drag inside to move it, drag a corner or edge to resize, drag on the
 * sheet outside it to draw a new one. Right: the slide as it will look. "Zoom to drawing" guesses
 * the box (lib/drawingExtent.js); "Whole sheet area" goes back to the standard crop. Choices are
 * saved per sheet when the presentation is made, so the next copy starts from the same framing.
 *
 * Opened by the 'make-drb-presentation' event ({ msId }) — the milestone ⋯ menu on DAPC / hearing
 * milestones and the DRB row in Documents — through DrbPresentationHost, mounted once in OutlineView.
 * Data through services/documents.js.
 *
 * The first time someone opens the window it walks them through it step by step (the Captain's
 * report walkthrough pattern, CaptainReportGate.jsx): one part of the window lit, the rest faded,
 * a callout with Back / Next / Skip. Once per browser; "How this works" replays it.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { findDrawingCrop, AJ_DRAWING_AREA, SLIDE_DRAWING_ASPECT } from '../lib/drawingExtent.js'
import { getDrbPlan, makeDrbPresentation } from '../services/documents.js'

/** Listens for 'make-drb-presentation' and opens the window; the new copy joins that milestone's
 *  Documents cache when it's loaded. */
export function DrbPresentationHost() {
  const [msId, setMsId] = useState(null)
  useEffect(() => {
    const on = (e) => { if (e.detail?.msId) setMsId(e.detail.msId) }
    window.addEventListener('make-drb-presentation', on)
    return () => window.removeEventListener('make-drb-presentation', on)
  }, [])
  if (!msId) return null
  function made(res) {
    if (res.document && ctx.docCache && Array.isArray(ctx.docCache[msId])) {
      ctx.docCache[msId] = [...ctx.docCache[msId], res.document]
      window.dispatchEvent(new Event('outline-render'))
    }
    if (res.skipped && res.skipped.length) {
      import('./Dialog.jsx').then(m => m.alertDialog({ title: 'Some drawings were left out', message: 'These sheets could not be read and are not in the presentation: ' + res.skipped.join(', ') + '.' }))
    }
  }
  return createPortal(<DrbPresentationModal key={msId} msId={msId} onClose={() => setMsId(null)} onMade={made} />, document.body)
}

const CONSULTANT_PREFIX = /^(L|C|S|M|E|P|FP|SU|T)\d/
const KIND_LABEL = { site: 'Site', storypole: 'Story poles', area: 'Area plan', existing: 'Existing', plan: 'Floor plan', roof: 'Roof', elevation: 'Elevation', section: 'Section', landscape: 'Landscape' }
const VIEW_W = 1600   // render width of the sheet in the editor
const MIN = 0.03

// The walkthrough runs once per browser. Bump the version when the steps change.
const TOUR_KEY = 'aj-drb-window-tour:v1'
function readTourSeen() { try { return localStorage.getItem(TOUR_KEY) === '1' } catch { return false } }

// One entry per part of the window, in the order you use them. `target` names the ref the callout points at.
const TOUR_STEPS = [
  {
    target: 'list', title: 'These are the drawings from your latest DAPC set',
    body: 'A ticked sheet becomes a slide. The ones a board looks at (site plan, story poles, area plans, existing and new floor plans, roof, elevations, sections, landscape) start ticked. Notes, schedules and demolition plans start unticked. Tick or untick any sheet, and click a sheet to frame it.',
  },
  {
    target: 'sheet', title: 'Frame the drawing',
    body: 'The bright box is what the slide shows; everything dimmed is cut off. Drag inside the box to move it, drag a corner or an edge to resize it, or drag across the sheet to draw a new box. A site plan or a small floor plan reads much better zoomed in.',
  },
  {
    target: 'tools', title: 'Let it find the drawing for you',
    body: 'Zoom to drawing finds the drawing on this sheet and frames it. Whole sheet area goes back to the standard crop: the whole drawing area, without the notes column and title block. The guess is a starting point, so check it and adjust the box if it cut something off.',
  },
  {
    target: 'preview', title: 'This is the slide the board will see',
    body: "The drawing fills the left of the slide. The panel on the right fills in this drawing's numbers on its own: setbacks and coverage beside the site plan, each level's areas beside its plans, heights beside the roof, elevations and sections. The line under it says whether the box fills the slide.",
  },
  {
    target: 'zoomAll', title: 'Or do the whole set at once',
    body: 'Zoom all to their drawings frames every ticked sheet. Then click through the list and check each one; a sheet you have framed shows ZOOMED beside its number.',
  },
  {
    target: 'make', title: 'Make the presentation',
    body: "This saves a new, dated copy in the milestone's Documents and opens it. Your ticks and boxes are remembered per sheet, so after a revision the next copy starts from the same framing. The file is large, so send the planner the link rather than an attachment.",
  },
]

/** The crop the deck uses when none is saved (api/_drbDeck.js does the same). */
function defaultCrop(sheet, info) {
  if (!info) return null
  const aj = !CONSULTANT_PREFIX.test(String(sheet.number || '').toUpperCase()) && info.rotate === 0 && Math.abs(info.aspect - 1.5) < 0.03
  return aj ? AJ_DRAWING_AREA : null
}

// Sheets render with pdfium (WebAssembly, loaded with this window): Revit sheets are dense enough
// that pdf.js took ~30 s a sheet; pdfium takes well under one.
let pdfiumLib = null
function getPdfium() {
  if (!pdfiumLib) pdfiumLib = import('@hyzyla/pdfium/inline').then(m => m.PDFiumLibrary.init({ disableBase64Warning: true }))
  return pdfiumLib
}

async function renderSheet(url) {
  const res = await fetch(url)
  if (!res.ok) throw new Error('Could not download this sheet (HTTP ' + res.status + ').')
  const bytes = new Uint8Array(await res.arrayBuffer())
  // /Rotate, read the same way the deck reads it, so the standard crop matches
  let rotate = 0
  try {
    const { PDFDocument } = await import('pdf-lib')
    const d = await PDFDocument.load(bytes.slice(), { ignoreEncryption: true, updateMetadata: false })
    rotate = ((d.getPage(0).getRotation().angle % 360) + 360) % 360
  } catch { /* unreadable to pdf-lib — the deck will skip it too */ }
  const lib = await getPdfium()
  const doc = await lib.loadDocument(bytes)
  try {
    const page = doc.getPage(0)
    const { originalWidth: w, originalHeight: h } = page.getOriginalSize()   // as displayed
    const img = await page.render({ scale: VIEW_W / w, render: 'bitmap' })
    const canvas = document.createElement('canvas')
    canvas.width = img.width; canvas.height = img.height
    const g = canvas.getContext('2d')
    const out = g.createImageData(img.width, img.height)
    const src = img.data, dst = out.data
    for (let i = 0; i < src.length; i += 4) { dst[i] = src[i + 2]; dst[i + 1] = src[i + 1]; dst[i + 2] = src[i]; dst[i + 3] = 255 }   // BGRA -> RGBA
    g.putImageData(out, 0, 0)
    return { canvas, url: canvas.toDataURL('image/jpeg', 0.85), aspect: w / h, rotate }
  } finally {
    doc.destroy()
  }
}

function autoCrop(info, sheet) {
  const sw = 600, sh = Math.round(600 / info.aspect)
  const c = document.createElement('canvas'); c.width = sw; c.height = sh
  const g = c.getContext('2d'); g.drawImage(info.canvas, 0, 0, sw, sh)
  const px = g.getImageData(0, 0, sw, sh).data
  const gray = new Uint8Array(sw * sh)
  for (let i = 0; i < sw * sh; i++) gray[i] = (px[i * 4] * 0.3 + px[i * 4 + 1] * 0.59 + px[i * 4 + 2] * 0.11) | 0
  return findDrawingCrop(gray, sw, sh, { area: defaultCrop(sheet, info) || [0, 0, 1, 1], pageAspect: info.aspect })
}

export default function DrbPresentationModal({ msId, onClose, onMade }) {
  const [sheets, setSheets] = useState(null)
  const [error, setError] = useState('')
  const [sel, setSel] = useState(0)
  const [renders, setRenders] = useState({})      // sheetId -> info | { error }
  const [busy, setBusy] = useState('')
  const loading = useRef({})
  const [tour, setTour] = useState(null)            // null = not running, else step index
  const [callout, setCallout] = useState(null)      // { top, left, width }
  const tourOffered = useRef(false)
  const refs = { list: useRef(null), sheet: useRef(null), tools: useRef(null), preview: useRef(null), zoomAll: useRef(null), make: useRef(null) }

  useEffect(() => {
    let off = false
    getDrbPlan(msId).then(res => {
      if (off) return
      if (res.error) { setError(res.error); return }
      setSheets(res.sheets.map(s => ({ ...s, crop: s.crop || null, touched: false })))
      const firstIn = res.sheets.findIndex(s => s.include)
      setSel(firstIn < 0 ? 0 : firstIn)
    })
    return () => { off = true }
  }, [msId])

  function ensureRender(s) {
    if (!s || renders[s.sheetId] || loading.current[s.sheetId]) return
    loading.current[s.sheetId] = true
    renderSheet(s.pdfUrl)
      .then(info => setRenders(r => ({ ...r, [s.sheetId]: info })))
      .catch(err => setRenders(r => ({ ...r, [s.sheetId]: { error: err.message || 'Could not read this PDF.' } })))
  }
  const cur = sheets && sheets[sel]
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ensureRender(cur) }, [cur?.sheetId])

  function patch(i, p) { setSheets(list => list.map((s, k) => (k === i ? { ...s, ...p, touched: true } : s))) }

  async function zoomAll() {
    setBusy('Finding the drawing on each sheet…')
    for (let i = 0; i < sheets.length; i++) {
      const s = sheets[i]
      if (!s.include) continue
      let info = renders[s.sheetId]
      if (!info) {
        try { info = await renderSheet(s.pdfUrl); setRenders(r => ({ ...r, [s.sheetId]: info })) } catch { continue }
      }
      if (info.error) continue
      const c = autoCrop(info, s)
      if (c) patch(i, { crop: c })
    }
    setBusy('')
  }

  async function make() {
    setBusy('Making the presentation…')
    const choices = sheets.map(s => ({ sheetId: s.sheetId, include: s.include, crop: s.crop }))
    const res = await makeDrbPresentation(msId, choices)
    setBusy('')
    if (res.error) { setError(res.error); return }
    onMade && onMade(res)
    onClose()
  }

  const info = cur && renders[cur.sheetId]
  const ready = !!(info && !info.error)

  // First open: start the walkthrough once the first sheet is on screen, so every step has its target.
  useEffect(() => {
    if (ready && !tourOffered.current) { tourOffered.current = true; if (!readTourSeen()) setTour(0) }
  }, [ready])
  const touring = tour != null
  const stepDef = touring ? TOUR_STEPS[tour] : null
  function endTour() {
    try { localStorage.setItem(TOUR_KEY, '1') } catch (_) { /* fine */ }
    setTour(null)
  }
  function tourNext() { if (tour >= TOUR_STEPS.length - 1) endTour(); else setTour(tour + 1) }
  function tourBack() { if (tour > 0) setTour(tour - 1) }

  // Place the callout next to the step's target. Measured, not guessed: the sheet list and the sheet
  // are taller than the screen leaves room for, so a tall target gets the callout beside it.
  useLayoutEffect(() => {
    if (!stepDef) { setCallout(null); return }
    const el = refs[stepDef.target].current
    if (!el) { setCallout(null); return }
    const place = () => {
      const r = el.getBoundingClientRect()
      const w = Math.min(340, window.innerWidth - 32), H = 230, vw = window.innerWidth, vh = window.innerHeight
      const clampX = x => Math.max(16, Math.min(x, vw - w - 16))
      const clampY = y => Math.max(16, Math.min(y, vh - H - 16))
      if (r.height > vh * 0.45) {
        // tall: beside it, right if there's room, else left, else inside its top-left corner
        const left = r.right + 14 + w <= vw - 16 ? r.right + 14 : (r.left - 14 - w >= 16 ? r.left - 14 - w : r.left + 16)
        setCallout({ width: w, left, top: clampY(r.top + 24) })
      } else {
        const below = vh - r.bottom > H + 24
        setCallout({ width: w, left: clampX(r.left + r.width / 2 - w / 2), top: below ? r.bottom + 12 : clampY(r.top - H - 12) })
      }
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [tour, ready, sel])   // eslint-disable-line react-hooks/exhaustive-deps

  // While the walkthrough runs, the step's target is lit and the rest of the window is faded and inert.
  const spot = (key) => (!touring ? {} : stepDef.target === key ? S.spotOn : S.spotOff)
  const fadeAll = touring ? S.spotOff : {}

  const shownCrop = cur ? (cur.crop || defaultCrop(cur, info && !info.error ? info : null) || [0, 0, 1, 1]) : null
  const included = sheets ? sheets.filter(s => s.include).length : 0

  return (
    <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose() }}>
      <div style={S.card}>
        <div style={S.head}>
          <div>
            <div style={S.title}>DRB presentation</div>
            <div style={S.hint}>Tick the drawings that go in, and frame each one. The slide fills with whatever is inside the box.</div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {ready && !touring && <button type="button" style={S.linkBtn} onClick={() => setTour(0)}>How this works</button>}
            <button style={S.btnGhost} onClick={onClose} disabled={!!busy || touring}>Close</button>
          </div>
        </div>

        {error && <div style={S.err}>{error}</div>}
        {!sheets && !error && <div style={S.muted}>Reading the latest drawings…</div>}

        {sheets && (
          <div style={S.body}>
            <div ref={refs.list} style={{ ...S.list, ...spot('list') }}>
              {sheets.map((s, i) => (
                <div key={s.sheetId} onClick={() => setSel(i)}
                  style={{ ...S.row, background: i === sel ? 'var(--aj-bg-subtle)' : 'transparent', borderColor: i === sel ? 'var(--aj-accent)' : 'transparent' }}>
                  <input type="checkbox" checked={!!s.include} onClick={e => e.stopPropagation()}
                    onChange={e => patch(i, { include: e.target.checked })} />
                  <div style={{ minWidth: 0, flex: 1, opacity: s.include ? 1 : 0.55 }}>
                    <div style={S.num}>{s.number}{s.crop && <span style={S.zoomTag}>zoomed</span>}</div>
                    <div style={S.rowTitle}>{s.title}</div>
                  </div>
                  {s.kind && <span style={S.kind}>{KIND_LABEL[s.kind]}</span>}
                </div>
              ))}
            </div>

            <div style={S.editor}>
              {cur && (
                <>
                  <div style={S.toolbar}>
                    <span style={{ ...S.curTitle, ...fadeAll }}><b style={{ fontFamily: '"DM Mono",monospace' }}>{cur.number}</b> {cur.title}</span>
                    <span style={{ flex: 1 }} />
                    <span ref={refs.tools} style={{ display: 'flex', gap: 8, padding: 2, borderRadius: 6, ...spot('tools') }}>
                    <button style={S.btnGhost} disabled={!info || info.error || !!busy}
                      onClick={() => { const c = autoCrop(info, cur); if (c) patch(sel, { crop: c }); else patch(sel, { crop: null }) }}>Zoom to drawing</button>
                    <button style={S.btnGhost} disabled={!!busy} onClick={() => patch(sel, { crop: null })}>Whole sheet area</button>
                    </span>
                  </div>
                  {!info && <div style={S.muted}>Rendering {cur.number}…</div>}
                  {info && info.error && <div style={S.err}>{info.error}</div>}
                  {info && !info.error && (
                    <div style={S.stage}>
                      <div ref={refs.sheet} style={{ flex: 1, minWidth: 0, alignSelf: 'flex-start', borderRadius: 4, ...spot('sheet') }}>
                        <CropEditor image={info.url} aspect={info.aspect} crop={shownCrop}
                          onChange={c => patch(sel, { crop: c })} />
                      </div>
                      <div ref={refs.preview} style={{ alignSelf: 'flex-start', borderRadius: 6, padding: 4, ...spot('preview') }}>
                        <SlidePreview info={info} crop={shownCrop} />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {sheets && (
          <div style={S.footer}>
            <span style={{ ...S.muted, ...fadeAll }}>{busy || `${included} drawing${included === 1 ? '' : 's'} in the presentation`}</span>
            <span style={{ flex: 1 }} />
            <span ref={refs.zoomAll} style={{ borderRadius: 6, padding: 2, ...spot('zoomAll') }}>
              <button style={S.btnGhost} disabled={!!busy} onClick={zoomAll}>Zoom all to their drawings</button>
            </span>
            <span ref={refs.make} style={{ borderRadius: 6, padding: 2, ...spot('make') }}>
              <button style={{ ...S.btnPrimary, opacity: busy || !included ? 0.5 : 1 }} disabled={!!busy || !included} onClick={make}>Make presentation</button>
            </span>
          </div>
        )}
      </div>

      {touring && callout && (
        <div style={{ ...S.callout, width: callout.width, left: callout.left, top: callout.top }} role="note" onMouseDown={e => e.stopPropagation()}>
          <div style={S.calloutStep}>Step {tour + 1} of {TOUR_STEPS.length}</div>
          <div style={S.calloutTitle}>{stepDef.title}</div>
          <div style={S.calloutBody}>{stepDef.body}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 12 }}>
            <button type="button" style={S.calloutSkip} onClick={endTour}>Skip</button>
            <div style={{ display: 'flex', gap: 8 }}>
              {tour > 0 && <button type="button" style={S.calloutGhost} onClick={tourBack}>Back</button>}
              <button type="button" style={S.calloutPrimary} onClick={tourNext}>{tour >= TOUR_STEPS.length - 1 ? 'Done, frame the sheets' : 'Next'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/** The sheet with a draggable crop box. `crop` is [l,t,r,b] fractions of the sheet. */
function CropEditor({ image, aspect, crop, onChange }) {
  const ref = useRef(null)
  const drag = useRef(null)
  const [, force] = useState(0)

  function frac(e) {
    const r = ref.current.getBoundingClientRect()
    return [Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))]
  }
  function down(e, mode) {
    e.preventDefault(); e.stopPropagation()
    const p = frac(e)
    drag.current = { mode, start: p, crop: [...crop] }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up, { once: true })
  }
  function move(e) {
    const d = drag.current
    if (!d) return
    const [x, y] = frac(e)
    const dx = x - d.start[0], dy = y - d.start[1]
    let [l, t, r, b] = d.crop
    if (d.mode === 'new') { l = Math.min(d.start[0], x); r = Math.max(d.start[0], x); t = Math.min(d.start[1], y); b = Math.max(d.start[1], y) }
    else if (d.mode === 'move') {
      const w = r - l, h = b - t
      l = Math.min(1 - w, Math.max(0, l + dx)); t = Math.min(1 - h, Math.max(0, t + dy)); r = l + w; b = t + h
    } else {
      if (d.mode.includes('l')) l = Math.min(r - MIN, Math.max(0, l + dx))
      if (d.mode.includes('r')) r = Math.max(l + MIN, Math.min(1, r + dx))
      if (d.mode.includes('t')) t = Math.min(b - MIN, Math.max(0, t + dy))
      if (d.mode.includes('b')) b = Math.max(t + MIN, Math.min(1, b + dy))
    }
    if (r - l < MIN || b - t < MIN) return
    onChange([l, t, r, b].map(v => Math.round(v * 10000) / 10000))
    force(n => n + 1)
  }
  function up() { drag.current = null; window.removeEventListener('pointermove', move) }
  useEffect(() => () => window.removeEventListener('pointermove', move), [])  // eslint-disable-line react-hooks/exhaustive-deps

  const [l, t, r, b] = crop
  const pct = v => (v * 100) + '%'
  const handles = [['tl', l, t], ['t', (l + r) / 2, t], ['tr', r, t], ['r', r, (t + b) / 2], ['br', r, b], ['b', (l + r) / 2, b], ['bl', l, b], ['l', l, (t + b) / 2]]
  const cursor = { tl: 'nwse-resize', br: 'nwse-resize', tr: 'nesw-resize', bl: 'nesw-resize', t: 'ns-resize', b: 'ns-resize', l: 'ew-resize', r: 'ew-resize' }
  return (
    <div style={S.sheetWrap}>
      <div ref={ref} onPointerDown={e => down(e, 'new')}
        style={{ position: 'relative', width: '100%', aspectRatio: String(aspect), cursor: 'crosshair', userSelect: 'none', touchAction: 'none', overflow: 'hidden' }}>
        <img src={image} alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', border: '0.5px solid var(--aj-border-mid)' }} />
        {/* dim outside the box */}
        <div style={{ position: 'absolute', left: pct(l), top: pct(t), width: pct(r - l), height: pct(b - t), boxShadow: '0 0 0 9999px rgba(7,39,45,0.42)', outline: '2px solid #22d3ee', cursor: 'move' }}
          onPointerDown={e => down(e, 'move')} />
        {handles.map(([k, x, y]) => (
          <div key={k} onPointerDown={e => down(e, k)}
            style={{ position: 'absolute', left: pct(x), top: pct(y), width: 12, height: 12, marginLeft: -6, marginTop: -6, background: '#fff', border: '2px solid #22d3ee', borderRadius: 2, cursor: cursor[k] }} />
        ))}
      </div>
    </div>
  )
}

/** The drawing slide as the deck lays it out: the cropped drawing fitted into 612 x 500 beside the panel. */
function SlidePreview({ info, crop }) {
  const ref = useRef(null)
  const key = useMemo(() => crop.join(','), [crop])
  useEffect(() => {
    const c = ref.current
    if (!c) return
    const W = 480, H = 270, k = W / 960
    c.width = W; c.height = H
    const g = c.getContext('2d')
    g.fillStyle = '#fff'; g.fillRect(0, 0, W, H)
    g.fillStyle = '#f4f7f8'; g.fillRect(652 * k, 0, W - 652 * k, H)
    const [l, t, r, b] = crop
    const sx = l * info.canvas.width, sy = t * info.canvas.height, sw = (r - l) * info.canvas.width, sh = (b - t) * info.canvas.height
    const ax = 20 * k, ay = 20 * k, aw = 612 * k, ah = 500 * k
    const s = Math.min(aw / sw, ah / sh)
    const dw = sw * s, dh = sh * s
    g.drawImage(info.canvas, sx, sy, sw, sh, ax + (aw - dw) / 2, ay + (ah - dh) / 2, dw, dh)
    g.strokeStyle = '#d6dee0'; g.lineWidth = 0.5; g.strokeRect(ax + (aw - dw) / 2, ay + (ah - dh) / 2, dw, dh)
    g.fillStyle = '#07272D'; g.fillRect(680 * k, 70 * k, 120 * k, 18 * k)
    g.fillStyle = '#c9d3d5'; for (let i = 0; i < 5; i++) g.fillRect(680 * k, (130 + i * 34) * k, 240 * k, 8 * k)
  }, [info, key])  // eslint-disable-line react-hooks/exhaustive-deps
  const [l, t, r, b] = crop
  const ratio = (((r - l) * info.aspect) / (b - t)) / SLIDE_DRAWING_ASPECT
  const fill = Math.min(ratio, 1 / ratio)
  return (
    <div style={S.preview}>
      <div style={S.previewLbl}>ON THE SLIDE</div>
      <canvas ref={ref} style={{ width: '100%', height: 'auto', display: 'block', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4 }} />
      <div style={S.muted}>{fill > 0.97 ? 'The drawing fills the slide.' : `The box is ${ratio > 1 ? 'wider' : 'taller'} than the slide, so some of the slide stays blank.`}</div>
    </div>
  )
}

const S = {
  overlay: { position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  card: { width: 'min(1500px, calc(100vw - 32px))', height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-surface)', borderRadius: 10, border: '0.5px solid var(--aj-border-mid)', boxShadow: '0 12px 40px rgba(0,0,0,0.35)', padding: 18, boxSizing: 'border-box' },
  head: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 },
  title: { fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--aj-text)' },
  hint: { marginTop: 4, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)' },
  err: { marginTop: 10, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: '#c0392b' },
  muted: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 6 },
  body: { flex: 1, minHeight: 0, display: 'flex', gap: 14, marginTop: 14 },
  list: { width: 270, flexShrink: 0, overflowY: 'auto', borderRight: '0.5px solid var(--aj-border)', paddingRight: 8 },
  row: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 4, borderLeft: '2px solid transparent', cursor: 'pointer' },
  num: { fontFamily: '"DM Mono",monospace', fontSize: 12, color: 'var(--aj-text)' },
  zoomTag: { marginLeft: 6, fontFamily: '"DM Sans",sans-serif', fontSize: 9.5, color: 'var(--aj-accent-blue)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  rowTitle: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  kind: { fontFamily: '"DM Sans",sans-serif', fontSize: 9.5, color: 'var(--aj-text-muted)', whiteSpace: 'nowrap' },
  editor: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  toolbar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 },
  curTitle: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' },
  stage: { flex: 1, minHeight: 0, display: 'flex', gap: 14, overflow: 'auto' },
  sheetWrap: { flex: 1, minWidth: 0 },
  preview: { width: 300, flexShrink: 0 },
  previewLbl: { fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 9.5, letterSpacing: '0.08em', color: 'var(--aj-text-muted)', marginBottom: 6 },
  footer: { marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 },
  btnGhost: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'transparent', color: 'var(--aj-text-mid)', cursor: 'pointer' },
  btnPrimary: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 4, border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer' },
  linkBtn: { font: 'inherit', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 },
  // Walkthrough (the Captain's report pattern): the lit target and the faded rest.
  spotOn: { boxShadow: '0 0 0 2px #b8540a, 0 0 0 7px rgba(184,84,10,0.14)', position: 'relative', zIndex: 2, background: 'var(--aj-bg-surface)' },
  spotOff: { opacity: 0.28, pointerEvents: 'none', filter: 'grayscale(0.4)' },
  callout: { position: 'fixed', zIndex: 1000, background: '#07272D', color: '#fff', borderRadius: 10, padding: '14px 16px', boxShadow: '0 16px 40px rgba(4,21,24,0.45)', boxSizing: 'border-box' },
  calloutStep: { fontFamily: '"Montserrat", sans-serif', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#f0b27a', marginBottom: 6 },
  calloutTitle: { fontFamily: '"DM Sans", sans-serif', fontSize: 14, fontWeight: 600, lineHeight: 1.35, marginBottom: 6 },
  calloutBody: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, lineHeight: 1.5, color: 'rgba(255,255,255,0.85)' },
  calloutSkip: { font: 'inherit', fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'rgba(255,255,255,0.7)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 },
  calloutGhost: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 4, border: '0.5px solid rgba(255,255,255,0.4)', background: 'transparent', color: '#fff', cursor: 'pointer' },
  calloutPrimary: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 4, border: 'none', background: '#fff', color: '#07272D', cursor: 'pointer' },
}
